import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import * as notificationService from "@/services/notificationService";
import * as RequestService from "@/services/RequestService";
import { NOTIFICATION_EVENTS, subscribeToNotificationEvent, emitRequestDeclined, emitRefreshNotifications } from "@/utils/notificationEvents";
import "./NotificationDropdown.css";

import NotificationIcon from "@/assets/notification-bing.svg";
import NoNotificationImage from "@/assets/ notification/Frame 1000004864.png";
import RequestIcon from "@/assets/ notification/Group.png";
import MessageIcon from "@/assets/ notification/Frame (1).png";
import { toPagePx } from '@/utils/pageZoom';

const NotificationDropdown = ({ userId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  
  const dropdownRef = useRef(null);
  const portalRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  const updateDropdownPosition = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    setDropdownPosition(
      toPagePx({
        top: rect.bottom + 8,
        right: viewportWidth - rect.right,
      })
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        portalRef.current &&
        !portalRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        updateDropdownPosition();
      }
    };

    const handleResize = () => {
      if (isOpen) {
        updateDropdownPosition();
      }
    };

    if (userId) {
      fetchUnreadCount();
    }

    if (isOpen && userId) {
      fetchNotifications();
      updateDropdownPosition();
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [userId, isOpen]);

  useEffect(() => {
    if (!userId) return;

    const handleRequestAccepted = async (event) => {
      const { requestId, fromUserId } = event.detail;
      
      const connectionRequestNotif = notifications.find(
        notif => notif.type === "connection_request" && 
                 !notif.read &&
                 (!fromUserId || notif.from_user_id === fromUserId)
      );
      if (connectionRequestNotif) {
        try {
          await notificationService.markAsRead(connectionRequestNotif.id);
        } catch (error) {
          console.error("Error marking notification as read:", error);
        }
      }
      
      fetchNotifications(1);
      fetchUnreadCount();
    };

    const handleRequestDeclined = async (event) => {
      const { requestId, fromUserId } = event.detail;
      console.log("Request declined event received:", requestId, fromUserId);
      
      const connectionRequestNotif = notifications.find(
        notif => notif.type === "connection_request" && 
                 !notif.read &&
                 (!fromUserId || notif.from_user_id === fromUserId)
      );
      
      if (connectionRequestNotif) {
        try {
          await notificationService.markAsRead(connectionRequestNotif.id);
          console.log("Marked notification as read:", connectionRequestNotif.id);
        } catch (error) {
          console.error("Error marking notification as read:", error);
        }
      }
      
      fetchNotifications(1);
      fetchUnreadCount();
    };

    const handleRefreshNotifications = () => {
      fetchNotifications(1);
      fetchUnreadCount();
    };

    const unsubscribeAccepted = subscribeToNotificationEvent(
      NOTIFICATION_EVENTS.REQUEST_ACCEPTED,
      handleRequestAccepted
    );
    const unsubscribeDeclined = subscribeToNotificationEvent(
      NOTIFICATION_EVENTS.REQUEST_DECLINED,
      handleRequestDeclined
    );
    const unsubscribeRefresh = subscribeToNotificationEvent(
      NOTIFICATION_EVENTS.REFRESH_NOTIFICATIONS,
      handleRefreshNotifications
    );

    return () => {
      unsubscribeAccepted();
      unsubscribeDeclined();
      unsubscribeRefresh();
    };
  }, [userId, notifications]);

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationService.getUnreadCount(userId);
      
      let count = 0;
      
      if (response?.success && response?.data) {
        count = response.data.count || 0;
      } else if (response?.data?.count !== undefined) {
        count = response.data.count;
      } else if (response?.count !== undefined) {
        count = response.count;
      } else if (typeof response?.data === 'number') {
        count = response.data;
      }
      
      console.log("Parsed unread count:", count);
      setUnreadCount(count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
      console.error("Error details:", error.response?.data);
    }
  };

  const fetchNotifications = async (pageNum = 1) => {
    if (loading) return;
    
    try {
      setLoading(true);
      const response = await notificationService.getUserNotifications(userId, pageNum, 20);
      
      console.log("Notification API Response:", response);
      
      
      let newNotifications = [];
      
      if (response?.success && response?.data) {
        if (Array.isArray(response.data)) {
          newNotifications = response.data;
        }
      }
      
      console.log("Parsed notifications:", newNotifications);
      if (newNotifications.length > 0) {
        console.log("Sample notification structure:", newNotifications[0]);
      }
      
      if (pageNum === 1) {
        setNotifications(newNotifications);
      } else {
        setNotifications(prev => [...prev, ...newNotifications]);
      }
      
      setHasMore(newNotifications.length === 20);
      setPage(pageNum);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      console.error("Error details:", error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleClearAll = async () => {
    try {
      await notificationService.markAllAsRead();
      
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, read: true }))
      );
      
      setUnreadCount(0);
    } catch (error) {
      console.error("Error clearing all notifications:", error);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await handleMarkAsRead(notification.id);
    }
    
    if (notification.type === "connection_request") {
      navigate("/messages", { 
        state: { 
          fromConnectionRequest: true,
          activeTab: "requests",
          showRequests: true
        } 
      });
    } else {
      navigate("/messages");
    }
    
    setIsOpen(false);
  };

  const handleDeclineConnectionRequest = async (notification, e) => {
    e.stopPropagation();
    
    try {
      const fromUserId = notification.from_user_id || notification.fromUserId;  
      if (!fromUserId || !userId) {
        console.error("Missing user IDs to find connection request");
        return;
      } 
      const requestsResponse = await RequestService.fetchReceivedRequests(userId);
      const requests = requestsResponse?.data?.data || requestsResponse?.data || [];
      const matchingRequest = requests.find(
        req => req.request_from === fromUserId && req.status === 'pending'
      );
      
      if (!matchingRequest) {
        console.error("Could not find matching connection request");
        await handleMarkAsRead(notification.id);
        return;
      }
      await RequestService.rejectConnectionRequest(matchingRequest.id);   
      await handleMarkAsRead(notification.id);     
      emitRequestDeclined(matchingRequest.id, fromUserId);
      emitRefreshNotifications();   
      fetchNotifications(1);
      fetchUnreadCount();
    } catch (error) {
      console.error("Error declining connection request:", error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "connection_request":
        return RequestIcon;
      case "message":
        return MessageIcon;
      case "product_inquiry":
      case "send_inquiry":
        return MessageIcon;
      case "requirement_inquiry":
      case "active_requirement":
        return MessageIcon;
      default:
        return RequestIcon;
    }
  };

  const getNotificationLabel = (type) => {
    switch (type) {
      case "connection_request":
        return "Request";
      case "message":
        return "Message";
      case "product_inquiry":
      case "send_inquiry":
        return "Product Inquiry";
      case "requirement_inquiry":
      case "active_requirement":
        return "Active Requirement";
      default:
        return "Notification";
    }
  };

  const getCompanyName = (notification) => {
    if (notification.fromCompany?.company_name) {
      return notification.fromCompany.company_name;
    }
    if (notification.company?.company_name) {
      return notification.company.company_name;
    }
    if (notification.companyName) {
      return notification.companyName;
    }
    if (notification.message && notification.message.includes('User ')) {
      if (notification.sender?.company_name) {
        return notification.sender.company_name;
      }
      if (notification.from_company_name) {
        return notification.from_company_name;
      }
    }
    
    return 'Unknown Company';
  };

  const getCompanyLogo = (notification) => {
    if (notification.fromCompany?.company_logo) {
      return notification.fromCompany.company_logo;
    }
    if (notification.company?.company_logo) {
      return notification.company.company_logo;
    }
    if (notification.sender?.company_logo) {
      return notification.sender.company_logo;
    }
    return null;
  };

  const getCompanyInitial = (notification) => {
    const companyName = getCompanyName(notification);
    return companyName?.[0]?.toUpperCase() || 'C';
  };

  const getFormattedMessage = (notification) => {
    let message = notification.message || '';
    const companyName = getCompanyName(notification);
    if (message.includes('User ') && companyName !== 'Unknown Company') {
      message = message.replace(/User \d+/g, companyName);
    }
    
    return message;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    if (diffInDays === 1) return "Yesterday";
    if (diffInDays < 7) return `${diffInDays} days ago`;
    
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const time = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    return `${dayName} ${time}`;
  };



  return (
    <>
      <div className="notification-container" ref={buttonRef}>
        <button
          className={`notification-button ${isOpen ? "is-active" : ""}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Notifications"
        >
          <img src={NotificationIcon} alt="Notifications" className="notification-icon" />
          {unreadCount > 0 && (
            <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div
            className="notification-dropdown"
            ref={portalRef}
            style={{
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`,
            }}
          >
            <div className="notification-header">
              <h3 className="notification-title">Notification</h3>
              {notifications.length > 0 && (
                <button
                  className="notification-clear-btn"
                  onClick={handleClearAll}
                >
                  Clear all
                </button>
              )}
            </div>

            <div className="notification-list">
              {loading && notifications.length === 0 ? (
                <div className="notification-loading">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="notification-empty">
                  <img
                    src={NoNotificationImage}
                    alt="No notifications"
                    className="notification-empty-image"
                  />
                </div>
              ) : (
                <>
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`notification-item ${!notification.read ? 'unread' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="notification-item-header">
                        <div className="notification-company-info">
                          {getCompanyLogo(notification) ? (
                            <img
                              src={getCompanyLogo(notification)}
                              alt={getCompanyName(notification)}
                              className="notification-company-logo"
                            />
                          ) : (
                            <div className="notification-company-logo-placeholder">
                              {getCompanyInitial(notification)}
                            </div>
                          )}
                          <div className="notification-company-details">
                            <h4 className="notification-company-name">
                              {getCompanyName(notification)}
                            </h4>
                            <p className="notification-time">
                              {formatDateTime(notification.created_at)}
                            </p>
                          </div>
                        </div>
                        <span className={`notification-type-badge ${notification.type}`}>
                          <img
                            src={getNotificationIcon(notification.type)}
                            alt={getNotificationLabel(notification.type)}
                            className="notification-type-icon"
                          />
                          {getNotificationLabel(notification.type)}
                        </span>
                      </div>

                      <p className="notification-message">{getFormattedMessage(notification)}</p>

                      {notification.type === "connection_request" && !notification.read && (
                        <div className="notification-actions">
                          <button
                            className="notification-action-btn notification-action-accept"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNotificationClick(notification);
                            }}
                          >
                            Accept
                          </button>
                          <button
                            className="notification-action-btn notification-action-decline"
                            onClick={(e) => handleDeclineConnectionRequest(notification, e)}
                          >
                            Decline
                          </button>
                        </div>
                      )}

                      {notification.type === "message" && (
                        <button
                          className="notification-reply-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNotificationClick(notification);
                          }}
                        >
                          Reply Now
                        </button>
                      )}

                      <p className="notification-relative-time">{formatTime(notification.created_at)}</p>
                    </div>
                  ))}

                  {hasMore && (
                    <button
                      className="notification-load-more"
                      onClick={() => fetchNotifications(page + 1)}
                      disabled={loading}
                    >
                      {loading ? 'Loading...' : 'Load More'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default NotificationDropdown;
